import type { ReactElement } from 'react';
import type { Mode } from '../api/schemas';
import { MODE_DESCRIPTION, MODE_LABEL } from '../domain/labels';

const MODIFIER: Readonly<Record<Mode, string>> = Object.freeze({
  PAPER: 'paper',
  VST: 'vst',
  LIVE: 'live',
});

const GLYPH: Readonly<Record<Mode, string>> = Object.freeze({
  PAPER: '📄',
  VST: '🧪',
  LIVE: '⚠',
});

export interface ModeBadgeProps {
  readonly mode: Mode;
}

/**
 * The most important pixel in the app.
 *
 * `LIVE` is filled danger-red with white text, a warning glyph, uppercase
 * lettering and the words «РЕАЛЬНЫЕ ДЕНЬГИ» — it cannot be mistaken for the
 * grey `PAPER` chip at a glance, in greyscale, or by a colour-blind operator.
 * It is also announced as an assertive live region so a mode change is not
 * silent for a screen-reader user.
 */
export function ModeBadge({ mode }: ModeBadgeProps): ReactElement {
  const isLive = mode === 'LIVE';
  return (
    <span
      className={`mode-badge mode-badge--${MODIFIER[mode]}`}
      role="status"
      aria-live={isLive ? 'assertive' : 'polite'}
      data-testid="mode-badge"
      data-mode={mode}
    >
      <span className="mode-badge__glyph" aria-hidden="true">
        {GLYPH[mode]}
      </span>
      <span className="visually-hidden">Режим работы: </span>
      <span>{MODE_LABEL[mode]}</span>
      <span aria-hidden="true">—</span>
      <span>{MODE_DESCRIPTION[mode]}</span>
    </span>
  );
}
